import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';
import { Building2, Cog, Wrench, BookOpen, DollarSign, Users, ChevronRight, Check, X } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const Architecte = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Configuration
  const seoConfig = {
    title: 'Architecture Commerciale - FOMOW | Ingénierie & Systèmes de Vente',
    description: 'Conception d\'architectures commerciales sur mesure : organisation équipes, process de vente, CRM, playbooks. Structurez une machine de vente scalable.',
    path: '/services/architecte',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Ingénierie & Systèmes Commerciaux",
      "description": "Conception d'architecture commerciale et systèmes de vente scalables",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Business Consulting"
    }
  };

  // Expertises à la carte
  const expertises = [
    { 
      icon: Building2, 
      title: 'Organisation commerciale', 
      desc: 'Structure des équipes, rôles, territoires, reporting lines',
      details: ['Définition des rôles SDR/AE/AM', 'Découpage des territoires', 'Organigramme et management']
    },
    { 
      icon: Cog, 
      title: 'Process de vente', 
      desc: 'Étapes du cycle, qualification, handoffs, SLA internes',
      details: ['Pipeline stages', 'Critères de qualification', 'Handoffs inter-équipes']
    },
    { 
      icon: Wrench, 
      title: 'Outillage & CRM', 
      desc: 'Sélection, configuration, intégrations, automatisations',
      details: ['Audit de l\'existant', 'Configuration CRM', 'Automatisations']
    },
    { 
      icon: BookOpen, 
      title: 'Playbooks & Scripts', 
      desc: 'Méthodologies, templates, séquences, objections',
      details: ['Scripts de prospection', 'Gestion des objections', 'Séquences email']
    },
    { 
      icon: DollarSign, 
      title: 'Compensation & Incentives', 
      desc: 'Plans de rémunération, quotas, accélérateurs',
      details: ['Variable et fixe', 'Définition des quotas', 'Mécaniques d\'accélération']
    },
    { 
      icon: Users, 
      title: 'Onboarding & Ramp-up', 
      desc: 'Parcours d\'intégration, formation initiale, KPIs de montée',
      details: ['Programme 30-60-90', 'Checklist d\'intégration', 'KPIs de ramp-up']
    },
  ];

  // Phases de travail
  const phases = [
    { 
      num: '01', 
      title: 'Immersion', 
      desc: 'On écoute, on observe, on comprend.',
      items: [
        'Échanges avec dirigeants et équipes terrain',
        'Analyse des données existantes (pipeline, conversion, cycle)',
        'Compréhension de votre culture d\'entreprise'
      ]
    },
    { 
      num: '02', 
      title: 'Diagnostic', 
      desc: 'On cartographie l\'existant sans jugement.',
      items: [
        'Forces, faiblesses, opportunités',
        'Benchmark sectoriel (pour inspirer, pas copier)',
        'Identification des quick wins et chantiers de fond'
      ]
    },
    { 
      num: '03', 
      title: 'Co-construction', 
      desc: 'On conçoit ensemble, pas pour vous.',
      items: [
        'Ateliers de travail avec vos équipes',
        'Proposition de plusieurs scénarios',
        'Validation itérative avec vous'
      ]
    },
    { 
      num: '04', 
      title: 'Déploiement', 
      desc: 'On accompagne jusqu\'à l\'adoption réelle.',
      items: [
        'Mise en place progressive',
        'Formation et conduite du changement',
        'Suivi des indicateurs et ajustements'
      ]
    },
  ];


  // Comparaison
  const comparisons = [
    { bad: 'Appliquer un framework standard à tous', good: 'Adapter les méthodologies à votre contexte' },
    { bad: 'Imposer des outils "tendance"', good: 'Recommander ce qui s\'intègre à votre stack' },
    { bad: 'Livrer un rapport et partir', good: 'Accompagner jusqu\'à l\'adoption réelle' },
    { bad: 'Promettre des résultats magiques', good: 'Définir des objectifs réalistes ensemble' },
  ];

  // Exemples de missions
  const missions = [
    { 
      type: 'Scale-up SaaS B2B', 
      size: '50 personnes',
      challenge: 'Passer de 3 à 15 commerciaux sans perdre en efficacité',
      solution: 'Structuration SDR/AE, playbooks, stack HubSpot',
      result: 'Équipe opérationnelle en 3 mois'
    },
    { 
      type: 'Cabinet de conseil', 
      size: '20 personnes',
      challenge: 'Formaliser un process de vente inexistant',
      solution: 'Définition du cycle, création CRM, formation équipe',
      result: 'Pipeline visible et prédictible'
    },
    { 
      type: 'Startup early-stage', 
      size: '10 personnes',
      challenge: 'Recruter les premiers commerciaux et les outiller',
      solution: 'Profils cibles, scorecard, onboarding 30-60-90',
      result: '2 recrutements réussis, ramp-up accéléré'
    },
  ];

  // FAQ
  const faqs = [
    { 
      q: 'Travaillez-vous avec des entreprises de toutes tailles ?', 
      a: 'Oui, de la startup early-stage au grand groupe. L\'approche s\'adapte à votre maturité et vos ressources. Ce qui compte, c\'est votre volonté de structurer.' 
    },
    { 
      q: 'Combien de temps dure une mission type ?', 
      a: 'Cela dépend du périmètre. Un audit ciblé peut prendre 2-3 semaines. Une refonte complète de l\'architecture commerciale, 2-4 mois. On définit ensemble le scope réaliste.' 
    },
    { 
      q: 'Faut-il avoir un CRM en place avant de commencer ?', 
      a: 'Non. On peut vous accompagner dans le choix et la mise en place. Mais si vous en avez déjà un, on optimise l\'existant avant de recommander un changement.' 
    },
    { 
      q: 'Comment mesurez-vous le succès d\'une mission ?', 
      a: 'On définit ensemble des KPIs concrets dès le départ : adoption des process, qualité du pipeline, temps de ramp-up, etc. Pas de vanity metrics.' 
    },
    { 
      q: 'Intervenez-vous aussi sur le recrutement et la formation ?', 
      a: 'Oui, via nos autres services dédiés. L\'architecture pose les fondations, mais on peut aussi vous accompagner sur l\'acquisition de talents et le coaching.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">Architecture Commerciale</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Ingénierie & Systèmes Commerciaux
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed mb-4">
              Votre organisation commerciale est <span className="text-[#2A41E5] font-semibold">unique</span>. 
              Nous construisons l'architecture qui correspond à votre réalité, pas un modèle standard.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-lg text-white/60 max-w-2xl mb-10">
              Pas de template. Pas de recette miracle. Une approche sur-mesure basée sur vos enjeux, votre maturité et vos ambitions.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Contactez-nous
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Expertises à la carte */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos expertises</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 text-center">
              Un catalogue d'expertises
            </h2>
            <p className="text-lg text-white/60 text-center max-w-3xl mx-auto mb-16">
              Choisissez les briques dont vous avez besoin. Nous les assemblons pour créer votre système sur-mesure.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertises.map((exp, idx) => {
              const IconComponent = exp.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-[#2A41E5]/50 transition-all duration-300 group h-full">
                    <div className="flex items-start gap-4 mb-4">
                      <IconComponent className="w-6 h-6 text-[#2A41E5] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{exp.title}</h3>
                        <p className="text-white/60 text-sm">{exp.desc}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 mt-4 pt-4 border-t border-white/10">
                      {exp.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#2A41E5]" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment on travaille */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Notre approche</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Une approche en 4 temps, adaptée à votre rythme
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Pas des étapes rigides, mais des phases flexibles qu'on adapte à votre contexte.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {phases.map((phase, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-[#2A41E5]/30">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#2A41E5] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <span className="text-white font-bold text-lg">{phase.num}</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{phase.title}</h3>
                      <p className="text-[#2A41E5] font-medium">{phase.desc}</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-600">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi cette approche */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Parce que les recettes magiques n'existent pas
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              On ne vous vend pas une méthode miracle. On construit avec vous ce qui marchera pour vous.
            </p>
          </AnimatedSection>
          
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2">
              {/* Ce qu'on NE fait PAS */}
              <div className="p-8 md:p-10 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2">
                  <X className="w-6 h-6 text-red-400" />
                  Ce qu'on NE fait PAS
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-gray-500">
                        <span className="text-red-400 mt-1">✗</span>
                        {comp.bad}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
              
              {/* Ce qu'on FAIT */}
              <div className="p-8 md:p-10 bg-[#041C2C]">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Ce qu'on FAIT
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-white/80">
                        <span className="text-green-400 mt-1">✓</span>
                        {comp.good}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples de missions */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Cas concrets</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Quelques exemples de missions
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Chaque mission est différente. Voici ce que nous avons construit pour d'autres.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {missions.map((mission, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <div className="mb-6">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#2A41E5]/10 text-[#2A41E5] text-sm font-medium mb-2">
                      {mission.type}
                    </div>
                    <p className="text-gray-500 text-sm">{mission.size}</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Enjeu</p>
                      <p className="text-gray-700 font-medium">{mission.challenge}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Ce qu'on a fait</p>
                      <p className="text-gray-600">{mission.solution}</p>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Résultat</p>
                      <p className="text-[#2A41E5] font-semibold">{mission.result}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#2A41E5]/30 transition-colors">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <svg 
                      className={`w-5 h-5 text-[#2A41E5] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à construire une organisation commerciale qui vous ressemble ?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Discutons de vos enjeux. On voit ensemble ce qui fait sens pour vous.<br />
              Sans engagement.
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Réserver un échange
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/sparring" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Stratégie & Sparring Partner</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Mentorat stratégique pour dirigeants. Un partenaire exigeant pour prendre du recul et décider vite.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/pilote" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Direction Commerciale Externalisée</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Pilotage opérationnel de bout en bout. On s'assure que la stratégie se transforme en chiffre d'affaires.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Architecte;
